/**
 * API client: single place for backend URL and HTTP calls.
 * Keeps UI decoupled from fetch/axios and allows swapping backend or adding auth later.
 */

import axios from "axios";
import { getAccessToken } from "@/lib/authToken";

/**
 * Backend base including /api. Accepts REACT_APP_BACKEND_URL with or without trailing /api
 * (e.g. https://api.example.com and https://api.example.com/api both work).
 */
export function getApiBaseUrl() {
  const raw = process.env.REACT_APP_BACKEND_URL;
  if (raw == null || String(raw).trim() === "") return "/api";
  let u = String(raw).trim().replace(/\/+$/, "");
  if (u.toLowerCase().endsWith("/api")) return u;
  return `${u}/api`;
}

const baseURL = getApiBaseUrl();

const client = axios.create({
  baseURL,
  paramsSerializer: (params) => {
    const parts = [];
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null) continue;
      if (Array.isArray(value)) {
        for (const v of value) {
          parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(v)}`);
        }
      } else {
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
      }
    }
    return parts.join("&");
  },
});

client.interceptors.request.use((config) => {
  const t = getAccessToken();
  if (t) {
    config.headers = { ...(config.headers || {}), Authorization: `Bearer ${t}` };
  }
  return config;
});

/** Use to show pricing paywall instead of a raw "Not authenticated" message. */
export function isUnauthenticatedError(err) {
  const status = err?.response?.status;
  if (status === 401) return true;
  const d = err?.response?.data?.detail;
  return typeof d === "string" && d.toLowerCase().includes("not authenticated");
}

function emitTrackerChanged(delta = 0) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("tracker:changed", { detail: { delta } }));
}

// Jobs
export async function getJobs(params = {}, options = {}) {
  const { data } = await client.get("/jobs", { params, ...options });
  return data;
}

export async function getJobsCount(params = {}, options = {}) {
  const { data } = await client.get("/jobs/count", { params, ...options });
  return data;
}

export async function getJob(id) {
  const raw = id == null ? "" : String(id).trim();
  if (!raw) {
    throw new Error("Missing job id");
  }
  const { data } = await client.get(`/jobs/${encodeURIComponent(raw)}`);
  return data;
}

/** Public job preview for signed-out users (no apply URL or full description). */
export async function getJobTeaser(id) {
  const raw = id == null ? "" : String(id).trim();
  if (!raw) {
    throw new Error("Missing job id");
  }
  const { data } = await client.get(`/jobs/${encodeURIComponent(raw)}/teaser`);
  return data;
}

export async function getFeaturedJobs() {
  const { data } = await client.get("/jobs/featured");
  return data;
}

export async function getRelatedJobs(jobId) {
  const raw = jobId == null ? "" : String(jobId).trim();
  if (!raw) return [];
  const { data } = await client.get(`/jobs/${encodeURIComponent(raw)}/related`);
  return data;
}

export async function getJobSidebar(jobId, companyHint, relatedLimit = 5, companyLimit = 4) {
  const raw = jobId == null ? "" : String(jobId).trim();
  if (!raw) {
    return { tracked: false, related_jobs: [], company_job_count: 0, other_jobs_at_company: [] };
  }
  const params = { related_limit: relatedLimit, company_limit: companyLimit };
  if (companyHint && String(companyHint).trim()) params.company_hint = String(companyHint).trim();
  const { data } = await client.get(`/jobs/${encodeURIComponent(raw)}/sidebar`, { params });
  return (
    data || { tracked: false, related_jobs: [], company_job_count: 0, other_jobs_at_company: [] }
  );
}

export async function getJobsByCompany(company, excludeId, limit = 4) {
  const params = { company: company?.trim() || "", limit };
  if (excludeId) params.exclude_id = excludeId;
  const { data } = await client.get("/jobs/by-company", { params });
  return Array.isArray(data) ? data : [];
}

// Track
export async function getTrackedJobs() {
  const { data } = await client.get("/track");
  return data;
}

export async function isJobTracked(jobId) {
  const raw = jobId == null ? "" : String(jobId).trim();
  if (!raw) return false;
  const { data } = await client.get(`/track/${encodeURIComponent(raw)}/exists`);
  return Boolean(data?.tracked);
}

export async function trackJob(payload) {
  const { data } = await client.post("/track", payload);
  emitTrackerChanged(1);
  return data;
}

export async function updateTrackedJob(trackId, payload) {
  const { data } = await client.put(`/track/${trackId}`, payload);
  return data;
}

export async function deleteTrackedJob(trackId) {
  await client.delete(`/track/${trackId}`);
  emitTrackerChanged(-1);
}

// Sync
export async function getSyncStatus() {
  const { data } = await client.get("/sync/status");
  return data;
}

// Resume / Cover letter
export async function getResume() {
  const { data } = await client.get("/resume");
  return data;
}

export async function saveResume(content) {
  const { data } = await client.post("/resume", { content });
  return data;
}

export async function uploadResume(file) {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await client.post("/resume/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 60000,
  });
  return data;
}

export async function updateResumeStructured(structuredData) {
  const { data } = await client.put("/resume/structured", { structured_data: structuredData });
  return data;
}

export async function createResumeFromStructured(structuredData) {
  const { data } = await client.post("/resume/from-structured", { structured_data: structuredData });
  return data;
}

export async function improveResumeText(type, text) {
  const { data } = await client.post("/resume/polish-text", { type, text });
  return data;
}

export async function getCoverLetter() {
  const { data } = await client.get("/cover-letter");
  return data;
}

export async function saveCoverLetter(contentOrPayload) {
  const body =
    typeof contentOrPayload === "string"
      ? { content: contentOrPayload }
      : contentOrPayload;
  const { data } = await client.post("/cover-letter", body);
  return data;
}

export async function uploadCoverLetter(file) {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await client.post("/cover-letter/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

// Generate
export async function generateResume(jobId, templateId) {
  const payload = { job_id: jobId };
  if (templateId) payload.template_id = templateId;
  const { data } = await client.post("/generate/resume", payload);
  return data;
}

export async function generateCoverLetter(jobId, templateId) {
  const payload = { job_id: jobId };
  if (templateId) payload.template_id = templateId;
  const { data } = await client.post("/generate/cover-letter", payload);
  return data;
}

export async function getApplicationKit(jobId) {
  const { data } = await client.get(`/jobs/${encodeURIComponent(jobId)}/application-kit`);
  return data;
}

export async function putApplicationKit(jobId, payload) {
  const { data } = await client.put(
    `/jobs/${encodeURIComponent(jobId)}/application-kit`,
    payload || {},
  );
  return data;
}

export async function generateDaySummary(jobId) {
  const { data } = await client.post("/generate/day-summary", { job_id: jobId });
  return data;
}

export async function generatePDF(structuredData, templateId, docType = "resume") {
  const { data } = await client.post("/generate/pdf", {
    structured_data: structuredData,
    template_id: templateId,
    doc_type: docType,
  });
  return data;
}

/** Trigger browser download of a PDF returned from {@link generatePDF}. */
export function downloadPdfBlob(pdfBase64, fileName = "resume.pdf") {
  if (!pdfBase64) return;
  const binary = atob(pdfBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  const blob = new Blob([bytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName || "resume.pdf";
  a.click();
  URL.revokeObjectURL(url);
}

export async function downloadResumePdf(structuredData, templateId = "ats_optimal") {
  const data = await generatePDF(structuredData, templateId, "resume");
  downloadPdfBlob(data.pdf_base64, data.file_name);
}

export async function listGeneratedDocs({ docType, jobId, summary } = {}) {
  const params = {};
  if (docType) params.doc_type = docType;
  if (jobId) params.job_id = jobId;
  if (summary) params.summary = true;
  const { data } = await client.get("/generated", { params });
  return Array.isArray(data) ? data : [];
}

export async function getGeneratedDoc(docId) {
  const { data } = await client.get(`/generated/${docId}`);
  return data;
}

export async function deleteGeneratedDoc(docId) {
  await client.delete(`/generated/${docId}`);
}

// Templates
export async function getTemplates(docType = "resume") {
  const { data } = await client.get("/templates", { params: { doc_type: docType } });
  return data;
}

// Auth (email session + JWT; no outbound email)
export async function signInWithEmail(payload) {
  const { data } = await client.post("/auth/session", payload);
  return data;
}

/**
 * After Stripe redirect while signed out: verify cs_..., sync subscription, create user by checkout email, return JWT.
 */
export async function completeCheckoutAuth(sessionId) {
  const { data } = await client.post("/auth/complete-checkout", {
    session_id: sessionId,
  });
  return data;
}

export async function getAuthMe() {
  const { data } = await client.get("/auth/me");
  return data;
}

/** Stripe Customer Portal (manage subscription); requires Bearer token. Returns { url }. */
export async function createBillingPortalSession() {
  const { data } = await client.post("/billing/customer-portal");
  return data;
}

/**
 * Create a Stripe Checkout Session (supports monthly trial via backend).
 * @param {"weekly"|"monthly"|"annual"} plan
 * @param {{ jobId?: string }} [opts]
 * @returns {Promise<{ url: string, session_id?: string }>}
 */
export async function createCheckoutSession(plan, opts = {}) {
  const body = { plan };
  const jobId = opts.jobId != null ? String(opts.jobId).trim() : "";
  if (jobId) body.job_id = jobId;
  const { data } = await client.post("/billing/create-checkout-session", body);
  return data;
}

/**
 * Sync subscription to our DB from a completed Checkout Session (cs_...).
 * Call after Stripe redirects back so access unlocks without waiting for webhooks.
 * Requires Bearer token; checkout email must match the signed-in account.
 */
export async function confirmCheckoutSession(sessionId) {
  const { data } = await client.post("/billing/confirm-checkout-session", {
    session_id: sessionId,
  });
  return data;
}


const ADMIN_TOKEN_STORAGE = "rareroles_admin_token";

/** Session-only admin JWT (from POST /admin/login); avoids clobbering the user's Authorization header. */
export function getStoredAdminToken() {
  try {
    return sessionStorage.getItem(ADMIN_TOKEN_STORAGE) || "";
  } catch {
    return "";
  }
}

export function setStoredAdminToken(token) {
  try {
    if (token) sessionStorage.setItem(ADMIN_TOKEN_STORAGE, token);
    else sessionStorage.removeItem(ADMIN_TOKEN_STORAGE);
  } catch {
    /* ignore */
  }
}

export function clearStoredAdminToken() {
  setStoredAdminToken(null);
}

/** Returns { access_token, token_type, expires_in }. Backend env: ADMIN_PASSWORD. */
export async function adminLogin(password) {
  const { data } = await client.post("/admin/login", { password });
  return data;
}

/** Admin metrics; sends X-Admin-Authorization so user JWT on the same request does not conflict. */
export async function getAdminMetrics() {
  const t = getStoredAdminToken();
  const headers = {};
  if (t) headers["X-Admin-Authorization"] = `Bearer ${t}`;
  const { data } = await client.get("/admin/metrics", { headers });
  return data;
}

/**
 * Admin Stripe net volume (BalanceTransaction sums). Either { preset } or { dateFrom, dateTo } (YYYY-MM-DD).
 */
export async function getAdminNetVolume(params = {}) {
  const t = getStoredAdminToken();
  const headers = {};
  if (t) headers["X-Admin-Authorization"] = `Bearer ${t}`;
  const query = {};
  if (params.dateFrom && params.dateTo) {
    query.date_from = params.dateFrom;
    query.date_to = params.dateTo;
  } else if (params.preset) {
    query.preset = params.preset;
  }
  const { data } = await client.get("/admin/net-volume", { headers, params: query });
  return data;
}

export { client };
